import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';
import {
  TextField,
  Button,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Grid,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const CustomerSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Debug API configuration
  useEffect(() => {
    console.log('🔧 CustomerSearch Component Loaded');
    console.log('🌐 API Endpoints:', API_ENDPOINTS);
    console.log('📡 Customers endpoint:', API_ENDPOINTS.customers);
    console.log('🏠 Current location:', window.location.href);
  }, []);

  // Fetch all customers from the backend
  const fetchAllCustomers = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🔍 Attempting to fetch customers from:', API_ENDPOINTS.customers);
      const response = await fetch(API_ENDPOINTS.customers);
      console.log('📡 Response received:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('✅ Customers data received:', data.length, 'customers');
      setAllCustomers(data);
      setCustomers(data);
    } catch (err) {
      console.error('❌ Error fetching customers:', err);
      setError('Failed to fetch customers from database');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch invoices for a specific customer
  const fetchCustomerInvoices = useCallback(async (customerId) => {
    if (!customerId) return;
    
    setInvoicesLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.invoicesByCustomer(customerId));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCustomerInvoices(data);
    } catch (err) {
      console.error('Error fetching customer invoices:', err);
      setCustomerInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  // Load all customers when component mounts
  useEffect(() => {
    fetchAllCustomers();
  }, [fetchAllCustomers]);

  // Fetch invoices when customer selection changes
  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerInvoices(selectedCustomer.id);
    } else {
      setCustomerInvoices([]);
    }
  }, [selectedCustomer, fetchCustomerInvoices]);

  const searchCustomers = useCallback(async () => {
    if (!searchTerm.trim()) {
      setCustomers(allCustomers);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Filter customers based on search term
      const filtered = allCustomers.filter(customer =>
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.includes(searchTerm) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      setCustomers(filtered);
    } catch (err) {
      setError('Failed to search customers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, allCustomers]);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchCustomers();
    } else if (searchTerm.length === 0) {
      setCustomers(allCustomers);
      setSelectedCustomer(null);
    }
  }, [searchTerm, searchCustomers, allCustomers]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setActiveTab(0); // Reset to first tab when selecting new customer
  };

  const handleUseCustomer = () => {
    if (selectedCustomer) {
      // In a real app, this would navigate to invoice creation with pre-filled data
      alert(`Customer ${selectedCustomer.name} selected. Redirecting to invoice creation...`);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getCustomerStatus = (totalInvoices, totalSpent) => {
    if (totalInvoices >= 5 && totalSpent >= 5000) return { text: 'VIP', color: 'success' };
    if (totalInvoices >= 3 && totalSpent >= 2000) return { text: 'Regular', color: 'info' };
    if (totalInvoices >= 1 && totalSpent >= 500) return { text: 'New', color: 'warning' };
    return { text: 'Prospect', color: 'default' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    } catch (e) {
      return `$${amount}`;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID': return 'success';
      case 'SENT': return 'info';
      case 'DRAFT': return 'warning';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Customer Management
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title="Search Customers" 
          action={
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={fetchAllCustomers}
              disabled={loading}
            >
              Refresh
            </Button>
          }
        />
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Search by Name, Phone, or Email"
                placeholder="Enter customer name, phone number, or email address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                helperText="Start typing to search for existing customers. Minimum 2 characters required."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button 
                variant="outlined" 
                startIcon={<SearchIcon />}
                onClick={searchCustomers}
                disabled={searchTerm.length < 2}
                fullWidth
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {customers.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title={`Customers (${customers.length} found)`} />
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Contact Info</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map(customer => (
                    <TableRow 
                      key={customer.id}
                      selected={selectedCustomer?.id === customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {customer.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {customer.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            📞 {customer.phone || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {customer.email || 'No email'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {customer.address || 'No address'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(customer.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={selectedCustomer?.id === customer.id ? "contained" : "outlined"}
                          color={selectedCustomer?.id === customer.id ? "success" : "primary"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomerSelect(customer);
                          }}
                        >
                          {selectedCustomer?.id === customer.id ? "Selected" : "Select"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {selectedCustomer && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title={`Customer: ${selectedCustomer.name || 'N/A'}`} />
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab 
                  icon={<PersonIcon />} 
                  label="Customer Details" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<ReceiptIcon />} 
                  label={`Invoices (${customerInvoices.length})`} 
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {/* Customer Details Tab */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Customer Details</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2"><strong>ID:</strong> {selectedCustomer.id}</Typography>
                    <Typography variant="body2"><strong>Name:</strong> {selectedCustomer.name || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Phone:</strong> {selectedCustomer.phone || 'N/A'}</Typography>
                    <Typography variant="body2"><strong>Email:</strong> {selectedCustomer.email || 'No email'}</Typography>
                    <Typography variant="body2"><strong>Address:</strong> {selectedCustomer.address || 'No address'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Account Information</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2"><strong>Customer Since:</strong> {formatDate(selectedCustomer.createdAt)}</Typography>
                    <Typography variant="body2"><strong>Last Updated:</strong> {formatDate(selectedCustomer.updatedAt)}</Typography>
                    <Typography variant="body2"><strong>Total Invoices:</strong> {customerInvoices.length}</Typography>
                  </Box>
                </Grid>
              </Grid>
            )}

            {/* Invoices Tab */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>Customer Invoices</Typography>
                {invoicesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : customerInvoices.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Invoice #</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Vehicle</TableCell>
                          <TableCell>Subtotal</TableCell>
                          <TableCell>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customerInvoices.map(invoice => (
                          <TableRow key={invoice.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {invoice.invoiceNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {formatDate(invoice.issueDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={invoice.status || 'DRAFT'} 
                                color={getStatusColor(invoice.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {invoice.carYear} {invoice.carMake} {invoice.carModel}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatCurrency(invoice.subtotal)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {formatCurrency(invoice.totalAmount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No invoices found for this customer.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button variant="contained" onClick={handleUseCustomer}>
                Use This Customer for New Invoice
              </Button>
              <Button variant="outlined" onClick={() => setSelectedCustomer(null)}>
                Clear Selection
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {searchTerm.length >= 2 && customers.length === 0 && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No customers found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              No customers match your search criteria.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Try searching with a different name, phone number, or email address.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && allCustomers.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No customers in database
            </Typography>
            <Typography variant="body2" color="textSecondary">
              The customers table appears to be empty.
            </Typography>
            <Button 
              variant="contained" 
              onClick={fetchAllCustomers}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CustomerSearch;
